/**
 * AULA INVERTIDA (M5) — editor del PLAN DE CLASE (profesor/admin).
 *
 * Para una `class_session`, elige módulos del CURSO de la cohorte y etiquétalos
 * antes/durante/después. Reutiliza el CourseModuleRegistry (directorio de módulos
 * con título resuelto). Escritura local-first → write-path (authz manager de la
 * cohorte + gate `academy.flipped_sessions`). Soft-delete con "Deshacer".
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Plus, Trash2 } from "lucide-solid";
import {
  addSessionModuleMutation,
  directoryRowTitle,
  isPlanPhase,
  type PlanPhase,
  removeSessionModuleMutation,
  restoreSessionModuleMutation,
  setSessionModulePhaseMutation,
} from "@space/shared";
import { useUndo } from "@/lib/undo";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useCourseModuleDirectory, useSessionPlanItems } from "../hooks/use-session-plan";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

const PHASE_LABELS: { value: PlanPhase; label: MessageDescriptor }[] = [
  { value: "pre", label: msg`Before` },
  { value: "live", label: msg`During` },
  { value: "post", label: msg`After` },
];

export const SessionPlanEditor: Component<{
  sessionId: string;
  cohortId: string;
  organizationId: string;
  campusId: string | null;
  courseId: string;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { registerUndo } = useUndo();

  const directory = useCourseModuleDirectory(() => props.courseId);
  const items = useSessionPlanItems(() => props.sessionId);

  const [moduleId, setModuleId] = createSignal("");
  const [phase, setPhase] = createSignal<PlanPhase>("pre");

  // Módulos del curso que aún NO están en el plan (evita duplicados; la UNIQUE
  // del server también lo garantiza).
  const inPlan = createMemo(() => new Set((items.data ?? []).map((i) => i.module_id)));
  const available = createMemo(() => (directory.data ?? []).filter((d) => !inPlan().has(d.id)));

  const titleFor = (id: string | null): string => {
    const row = (directory.data ?? []).find((d) => d.id === id);
    return (row && directoryRowTitle(row)) || t(msg`Module`);
  };

  const phaseLabel = (p: string | null) =>
    t(PHASE_LABELS.find((x) => x.value === p)?.label ?? msg`Before`);

  const addModule = async () => {
    const db = drizzle();
    const mid = moduleId();
    if (!db || !mid) {
      return;
    }
    // Posición = al final de su fase.
    const samePhase = (items.data ?? []).filter((i) => i.phase === phase());
    const position = samePhase.length;
    await addSessionModuleMutation(
      db,
      {
        sessionId: props.sessionId,
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        campusId: props.campusId,
      },
      { moduleId: mid, phase: phase(), position },
    );
    setModuleId("");
  };

  const changePhase = async (id: string, next: string) => {
    const db = drizzle();
    if (!db || !isPlanPhase(next)) {
      return;
    }
    await setSessionModulePhaseMutation(db, id, next);
  };

  const remove = (id: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    void (async () => {
      await removeSessionModuleMutation(db, id);
      registerUndo(t(msg`Removed from the class plan`), async () => {
        const d = drizzle();
        if (d) {
          await restoreSessionModuleMutation(d, id);
        }
      });
    })();
  };

  return (
    <div
      class="space-y-3 rounded-xl border border-border bg-muted/10 p-3"
      data-testid="session-plan-editor"
    >
      <h4 class="text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {t(msg`Class plan (before / during / after)`)}
      </h4>

      {/* Añadir módulo al plan */}
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
        <select
          class={field}
          data-testid="plan-add-module"
          value={moduleId()}
          onChange={(e) => setModuleId(e.currentTarget.value)}
        >
          <option value="">{t(msg`Pick a module…`)}</option>
          <For each={available()}>
            {(d) => <option value={d.id}>{directoryRowTitle(d) ?? t(msg`Module`)}</option>}
          </For>
        </select>
        <select
          class={field}
          data-testid="plan-add-phase"
          value={phase()}
          onChange={(e) => {
            const v = e.currentTarget.value;
            if (isPlanPhase(v)) {
              setPhase(v);
            }
          }}
        >
          <For each={PHASE_LABELS}>{(p) => <option value={p.value}>{t(p.label)}</option>}</For>
        </select>
        <Button data-testid="plan-add-btn" onClick={() => void addModule()} class="gap-1">
          <Plus class="h-4 w-4" /> {t(msg`Add`)}
        </Button>
      </div>

      {/* Ítems actuales del plan */}
      <Show
        when={(items.data ?? []).length > 0}
        fallback={
          <p class="text-xs text-muted-foreground">{t(msg`No modules in the plan yet.`)}</p>
        }
      >
        <ul class="space-y-1">
          <For each={items.data ?? []}>
            {(i) => (
              <li
                class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
                data-testid="plan-editor-item"
                data-module-id={i.module_id ?? ""}
                data-phase={i.phase ?? ""}
              >
                <span class="truncate">{titleFor(i.module_id)}</span>
                <span class="flex items-center gap-2">
                  <select
                    class="rounded-lg border border-border bg-field-background px-2 py-1 text-xs"
                    data-testid="plan-item-phase"
                    value={i.phase ?? "pre"}
                    aria-label={t(msg`Module phase`)}
                    onChange={(e) => void changePhase(i.id, e.currentTarget.value)}
                  >
                    <For each={PHASE_LABELS}>
                      {(p) => <option value={p.value}>{t(p.label)}</option>}
                    </For>
                  </select>
                  <span class="text-xs text-muted-foreground">{phaseLabel(i.phase)}</span>
                  <button
                    type="button"
                    data-testid="plan-item-remove"
                    onClick={() => remove(i.id)}
                    class="text-muted-foreground hover:text-danger"
                    aria-label={t(msg`Remove from plan`)}
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
};
